// MotorForm.js
import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  useTheme,
  Slider,
  Card,
  CardContent
} from "@mui/material";
import { User, Map, FileText, AlertCircle, Car, CreditCard, Fuel, Settings, Shield, Calendar, Award, Check, Building } from "lucide-react";
import { generateTenantToken, makeEncryptedApiCall } from "./utils/apiUtils";

const indianStates = ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"];
const vehicleWheelTypes = ["Two wheeler", "Four wheeler"];
const vehicleFuelTypes = ["CNG", "Petrol", "Diesel", "EV"];
const vehicleUsageTypes = ["Commercial", "Private"];
const insurancePolicyTypes = ["OD", "TP"];
const popularMakeModels = [
  "", // Empty option
  "Maruti Suzuki Swift",
  "Maruti Suzuki Alto",
  "Hyundai i20",
  "Tata Nexon",
  "Mahindra XUV700",
  "Honda City",
  "Toyota Innova",
  "Maruti Suzuki Baleno",
  "Hyundai Creta",
  "Kia Seltos",
  "Hero Splendor Plus",
  "Honda Activa",
  "Bajaj Pulsar",
  "TVS Apache",
  "Royal Enfield Classic"
];

const toTitleCase = (str) =>
  str
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const getFieldIcon = (fieldName) => {
  const iconMap = {
    age: <User size={20} />,
    gender: <User size={20} />,
    state: <Map size={20} />,
    credit_score: <Award size={20} />,
    has_addon: <FileText size={20} />,
    is_emi_payment: <CreditCard size={20} />,
    has_previous_claims: <AlertCircle size={20} />,
    vehicle_type_wheels: <Car size={20} />,
    vehicle_fuel_type: <Fuel size={20} />,
    vehicle_usage_type: <Settings size={20} />,
    puc_valid: <Shield size={20} />,
    previous_insurance_policy_type: <FileText size={20} />,
    insurance_policy_type: <Shield size={20} />,
    ownership_change: <User size={20} />,
    ncb_percentage: <Award size={20} />,
    policy_expired: <Calendar size={20} />,
    make_model_variant: <Car size={20} />,
    vehicle_age: <Calendar size={20} />,
    tenantId: <Building size={20} />,
    tenantName: <Building size={20} />
  };
  return iconMap[fieldName] || <Check size={20} />;
};

function MotorForm({ setScore, setErrorMessage, setOpenSnackbar, setIsLoading }) {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    age: "42",
    gender: "Male",
    state: "Tamil Nadu",
    credit_score: "680",
    has_addon: true,
    is_emi_payment: false,
    has_previous_claims: true,
    vehicle_type_wheels: "Four wheeler",
    vehicle_fuel_type: "Petrol",
    vehicle_usage_type: "Private",
    puc_valid: true,
    previous_insurance_policy_type: "OD",
    insurance_policy_type: "OD",
    ownership_change: false,
    ncb_percentage: 20,
    policy_expired: false,
    make_model_variant: "Maruti Suzuki Swift",
    vehicle_age: "3"
  });
  
  // Tenant information state with defaults
  const [tenantInfo, setTenantInfo] = useState({
    tenantId: "1",
    tenantName: ""
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Convert Yes/No to boolean for specific fields
    if (['has_addon', 'is_emi_payment', 'has_previous_claims', 'puc_valid', 'ownership_change', 'policy_expired'].includes(name)) {
      setFormData({ ...formData, [name]: value === 'Yes' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleTenantChange = (e) => {
    const { name, value } = e.target;
    setTenantInfo({ ...tenantInfo, [name]: value });
  };

  const handleSliderChange = (name) => (event, newValue) => {
    setFormData({ ...formData, [name]: newValue });
  };

  const handleMotorFormSubmit = async () => {
    setScore(null);
    setErrorMessage("");
    setIsSubmitting(true);
    setIsLoading(true);

    // Validate tenant information
    if (!tenantInfo.tenantId.trim() || !tenantInfo.tenantName.trim()) {
      setErrorMessage("Please enter both Tenant ID and Tenant Name.");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }

    const age = parseInt(formData.age, 10);
    const creditScore = formData.credit_score ? parseInt(formData.credit_score, 10) : null;
    const vehicleAge = formData.vehicle_age ? parseInt(formData.vehicle_age, 10) : null;

    // Validation
    if (isNaN(age) || age < 18 || age > 100) {
      setErrorMessage("Age must be between 18 and 100.");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }

    if (creditScore !== null && (isNaN(creditScore) || creditScore < 0 || creditScore > 850)) {
      setErrorMessage("Credit Score must be between 0 and 850.");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }

    if (vehicleAge !== null && (isNaN(vehicleAge) || vehicleAge < 0 || vehicleAge > 50)) {
      setErrorMessage("Vehicle Age must be between 0 and 50 years.");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }

    // Validate boolean fields
    if (formData.has_addon === null || formData.is_emi_payment === null || formData.has_previous_claims === null || 
        formData.puc_valid === null || formData.ownership_change === null || formData.policy_expired === null) {
      setErrorMessage("Please answer all Yes/No questions.");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }

    try {
      // Step 1: Generate tenant token
      const tokenResponse = await generateTenantToken(tenantInfo.tenantId, tenantInfo.tenantName);
      
      // Step 2: Prepare request data
      const requestData = {
        age: age,
        gender: formData.gender,
        state: formData.state,
        credit_score: creditScore,
        has_addon: formData.has_addon,
        is_emi_payment: formData.is_emi_payment,
        has_previous_claims: formData.has_previous_claims,
        vehicle_type_wheels: formData.vehicle_type_wheels,
        vehicle_fuel_type: formData.vehicle_fuel_type,
        vehicle_usage_type: formData.vehicle_usage_type,
        puc_valid: formData.puc_valid,
        previous_insurance_policy_type: formData.previous_insurance_policy_type || null,
        insurance_policy_type: formData.insurance_policy_type,
        ownership_change: formData.ownership_change,
        ncb_percentage: formData.ncb_percentage,
        policy_expired: formData.policy_expired,
        make_model_variant: formData.make_model_variant || null,
        vehicle_age: vehicleAge
      };

      // Step 3: Make encrypted API call
      const url = `/api/v1.0/tenants/${tenantInfo.tenantId}/score/motor`;
      const score = await makeEncryptedApiCall(url, requestData, tokenResponse.token);
      
      setScore(score.output);
    } catch (error) {
      console.error("Error fetching motor score:", error);
      let detailedMessage = "An error occurred while fetching the motor insurance score.";
      if (error.response && error.response.data && error.response.data.error) {
        detailedMessage = error.response.data.error;
      } else if (error.message) {
        detailedMessage = error.message;
      }
      setErrorMessage(detailedMessage);
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Tenant Information Card */}
      <Grid item xs={12}>
        <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Building size={20} />
              Tenant Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {getFieldIcon('tenantId')}
                  <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
                    Tenant ID
                  </Typography>
                </Box>
                <TextField
                  name="tenantId"
                  value={tenantInfo.tenantId}
                  onChange={handleTenantChange}
                  fullWidth
                  required
                  placeholder="Enter Tenant ID"
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {getFieldIcon('tenantName')}
                  <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
                    Tenant Name
                  </Typography>
                </Box>
                <TextField
                  name="tenantName"
                  value={tenantInfo.tenantName}
                  onChange={handleTenantChange}
                  fullWidth
                  required
                  placeholder="Enter Tenant Name"
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Basic Information */}
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('age')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Age
          </Typography>
        </Box>
        <TextField
          name="age"
          type="number"
          inputProps={{ min: 18, max: 100 }}
          value={formData.age}
          onChange={handleChange}
          fullWidth
          required
          placeholder="Enter age (18-100)"
          variant="outlined"
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          disabled={isSubmitting}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('gender')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Gender
          </Typography>
        </Box>
        <RadioGroup
          row
          name="gender"
          value={formData.gender}
          onChange={handleChange}
          sx={{ '& .MuiRadio-root': { color: theme.palette.primary.main } }}
        >
          <FormControlLabel value="Male" control={<Radio disabled={isSubmitting} size="small" />} label="Male" />
          <FormControlLabel value="Female" control={<Radio disabled={isSubmitting} size="small" />} label="Female" />
        </RadioGroup>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('state')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            State
          </Typography>
        </Box>
        <FormControl fullWidth disabled={isSubmitting} size="small">
          <Select
            name="state"
            value={formData.state}
            onChange={handleChange}
            displayEmpty
            variant="outlined"
            sx={{ borderRadius: 2 }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 2, boxShadow: '0 10px 40px rgba(0,0,0,0.1)', maxHeight: 300 } } }}
          >
            <MenuItem value="" disabled>Select State</MenuItem>
            {indianStates.map((state) => (
              <MenuItem key={state} value={state}>{state}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('credit_score')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Credit Score (Optional)
          </Typography>
        </Box>
        <TextField
          name="credit_score"
          type="number"
          inputProps={{ min: 0, max: 850 }}
          value={formData.credit_score}
          onChange={handleChange}
          fullWidth
          placeholder="Enter score (0-850)"
          variant="outlined"
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          disabled={isSubmitting}
        />
      </Grid>

      {/* Vehicle Information */}
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('vehicle_type_wheels')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Vehicle Type
          </Typography>
        </Box>
        <FormControl fullWidth disabled={isSubmitting} size="small">
          <Select
            name="vehicle_type_wheels"
            value={formData.vehicle_type_wheels}
            onChange={handleChange}
            variant="outlined"
            sx={{ borderRadius: 2 }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 2, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } } }}
          >
            {vehicleWheelTypes.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('vehicle_fuel_type')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Fuel Type
          </Typography>
        </Box>
        <FormControl fullWidth disabled={isSubmitting} size="small">
          <Select
            name="vehicle_fuel_type"
            value={formData.vehicle_fuel_type}
            onChange={handleChange}
            variant="outlined"
            sx={{ borderRadius: 2 }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 2, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } } }}
          >
            {vehicleFuelTypes.map((fuel) => (
              <MenuItem key={fuel} value={fuel}>{fuel}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('vehicle_usage_type')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Usage Type
          </Typography>
        </Box>
        <FormControl fullWidth disabled={isSubmitting} size="small">
          <Select
            name="vehicle_usage_type"
            value={formData.vehicle_usage_type}
            onChange={handleChange}
            variant="outlined"
            sx={{ borderRadius: 2 }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 2, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } } }}
          >
            {vehicleUsageTypes.map((usage) => (
              <MenuItem key={usage} value={usage}>{usage}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('make_model_variant')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Make/Model (Optional)
          </Typography>
        </Box>
        <FormControl fullWidth disabled={isSubmitting} size="small">
          <Select
            name="make_model_variant"
            value={formData.make_model_variant}
            onChange={handleChange}
            displayEmpty
            variant="outlined"
            sx={{ borderRadius: 2 }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 2, boxShadow: '0 10px 40px rgba(0,0,0,0.1)', maxHeight: 300 } } }}
          >
            <MenuItem value="">Select Make/Model</MenuItem>
            {popularMakeModels.slice(1).map((model) => (
              <MenuItem key={model} value={model}>{model}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('vehicle_age')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Vehicle Age (Optional)
          </Typography>
        </Box>
        <TextField
          name="vehicle_age"
          type="number"
          inputProps={{ min: 0, max: 50 }}
          value={formData.vehicle_age}
          onChange={handleChange}
          fullWidth
          placeholder="Enter vehicle age in years"
          variant="outlined"
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          disabled={isSubmitting}
        />
      </Grid>

      {/* Insurance Information */}
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('insurance_policy_type')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Insurance Policy Type
          </Typography>
        </Box>
        <FormControl fullWidth disabled={isSubmitting} size="small">
          <Select
            name="insurance_policy_type"
            value={formData.insurance_policy_type}
            onChange={handleChange}
            variant="outlined"
            sx={{ borderRadius: 2 }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 2, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } } }}
          >
            {insurancePolicyTypes.map((type) => (
              <MenuItem key={type} value={type}>{type === "OD" ? "Own Damage (OD)" : "Third Party (TP)"}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getFieldIcon('previous_insurance_policy_type')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            Previous Policy Type (Optional)
          </Typography>
        </Box>
        <FormControl fullWidth disabled={isSubmitting} size="small">
          <Select
            name="previous_insurance_policy_type"
            value={formData.previous_insurance_policy_type}
            onChange={handleChange}
            displayEmpty
            variant="outlined"
            sx={{ borderRadius: 2 }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 2, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } } }}
          >
            <MenuItem value="">No Previous Policy</MenuItem>
            <MenuItem value="OD">Own Damage (OD)</MenuItem>
            <MenuItem value="TP">Third Party (TP)</MenuItem>
            <MenuItem value="Comprehensive">Comprehensive</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* NCB Percentage Slider */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {getFieldIcon('ncb_percentage')}
          <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
            No Claim Bonus (NCB) Percentage: {formData.ncb_percentage}%
          </Typography>
        </Box>
        <Slider
          name="ncb_percentage"
          value={formData.ncb_percentage}
          onChange={handleSliderChange('ncb_percentage')}
          min={0}
          max={50}
          step={5}
          marks
          valueLabelDisplay="auto"
          disabled={isSubmitting}
          sx={{
            color: theme.palette.primary.main,
            '& .MuiSlider-thumb': {
              backgroundColor: theme.palette.primary.main,
            },
            '& .MuiSlider-track': {
              backgroundColor: theme.palette.primary.main,
            },
            '& .MuiSlider-rail': {
              backgroundColor: theme.palette.grey[300],
            }
          }}
        />
      </Grid>

      {/* Yes/No Fields */}
      {['has_addon', 'is_emi_payment', 'has_previous_claims', 'puc_valid', 'ownership_change', 'policy_expired'].map((field) => (
        <Grid item xs={12} sm={6} key={field}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {getFieldIcon(field)}
            <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
              {toTitleCase(field)}
            </Typography>
          </Box>
          <RadioGroup
            row
            name={field}
            value={formData[field] === null ? '' : (formData[field] ? 'Yes' : 'No')}
            onChange={handleChange}
            sx={{ '& .MuiRadio-root': { color: theme.palette.primary.main } }}
          >
            <FormControlLabel value="Yes" control={<Radio disabled={isSubmitting} size="small" />} label="Yes" />
            <FormControlLabel value="No" control={<Radio disabled={isSubmitting} size="small" />} label="No" />
          </RadioGroup>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Button
          variant="contained"
          onClick={handleMotorFormSubmit}
          fullWidth
          disabled={isSubmitting}
          size="large"
          sx={{
            mt: 2, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '1rem', fontWeight: 600,
            background: 'linear-gradient(135deg,rgb(88, 139, 249) 0%, #2563EB 100%)',
            boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
            '&:hover': { boxShadow: '0 15px 30px rgba(37, 99, 235, 0.3)', transform: 'translateY(-2px)' },
            transition: 'all 0.3s ease',
            '&.Mui-disabled': {
                background: theme.palette.grey[300],
                boxShadow: 'none',
                color: theme.palette.grey[500],
                cursor: 'not-allowed'
            }
          }}
        >
          {isSubmitting ? "Calculating..." : "Calculate Motor Insurance Score"}
        </Button>
      </Grid>
    </Grid>
  );
}

export default MotorForm;
